import { Suspense, useEffect } from 'react';

import { CookieConsentUsercentrics } from '@xing-com/cookie-consent-usercentrics';
import { DEFAULT_LAYOUT } from '@xing-com/crate-core-assets';
import type { Route as RouteData } from '@xing-com/crate-core-router';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { ScrollManager } from '@xing-com/crate-layout-scroll-memo-module';
import { XingletErrorBoundary } from '@xing-com/crate-runtime';
import { NoSSR } from '@xing-com/crate-utils-no-ssr';
import { useMetadata, XingletLoader } from '@xing-com/crate-xinglet';
import { useRuntime } from '@xing-com/crate-xinglet/internal';
import { ActiveItemContext, useViewerData } from '@xing-com/hub';

import { Generic, NoAccessPage } from './error-components';
import { initTracking } from './init-tracking';
import { LayoutSwitcher } from './layout-switcher';
import { LoadingSkeleton } from './loading-skeleton';
import { OnboardingEnforcementExperiment } from './onboarding-enforcement-experiment';
import { PageViewTracker } from './page-view-tracker/page-view-tracker';
import { PayOrConsent, usePayOrConsentState } from './pay-or-consent';
import { Zerofier } from './zerofier';

declare global {
  // eslint-disable-next-line no-var
  var DASH0_RUM:
    | {
        addSignalAttribute: (
          name: string,
          value: Record<string, string>
        ) => void;
        removeSignalAttribute: (name: string) => void;
      }
    | undefined;
}

export const RouteXinglet: React.FC<
  React.PropsWithChildren<{
    name: string;
    nwtTrackingPage?: string;
    nwtTrackingSentBy?: string;
  }>
> = ({ children, name, nwtTrackingPage, nwtTrackingSentBy }) => {
  const { ssr } = useMetadata(name);
  const { loading: loadingViewerData } = useViewerData();

  if (!ssr && loadingViewerData) {
    return <LoadingSkeleton />;
  }

  return (
    <>
      <PageViewTracker page={nwtTrackingPage} sentBy={nwtTrackingSentBy} />
      <Zerofier />
      {children}
    </>
  );
};

export type RouteProps = React.PropsWithChildren<{
  route: RouteData;
}>;

export const Route: React.FC<RouteProps> = ({ children, route }) => {
  // calls ttt.init and sets up the tracking environment
  // should always be the first call in the root provider
  const ttt = initTracking();

  const {
    activeItem,
    brazeSection,
    cacheMaxAge,
    disableCookieConsentBanner = false,
    disablePayOrConsentWall = false,
    layout,
    layoutLoggedIn,
    loginDestination,
    loginTracking,
    nwtTrackingPage,
    nwtTrackingSentBy,
    visibleTo: {
      loggedIn = true,
      loggedOut = false,
      softLoggedIn = false,
    } = {},
  } = route.config;

  const visibleTo = {
    loggedIn,
    loggedOut,
    softLoggedIn,
  };
  const xingletName = route.xinglet.replace('@xing-com/crate-', '');

  useEffect(() => {
    const dash0Rum = globalThis.DASH0_RUM;
    if (!dash0Rum) {
      return;
    }

    const xingletSignalAttribute: Record<string, string> = {
      name: xingletName,
      ...(nwtTrackingPage ? { pageName: nwtTrackingPage } : {}),
    };

    dash0Rum.removeSignalAttribute('xinglet');
    dash0Rum.addSignalAttribute('xinglet', xingletSignalAttribute);

    return () => {
      dash0Rum.removeSignalAttribute('xinglet');
    };
  }, [nwtTrackingPage, xingletName]);

  const runtime = useRuntime();
  const host = runtime.host;
  const loginState = useLoginState();
  const { isLoggedIn, isLoggedOut, isSoftLoggedIn } = loginState;
  const {
    shouldHoldConsentUi,
    shouldLoadUsercentricsInBackground,
    shouldRenderPayOrConsentPlaceholder,
  } = usePayOrConsentState();
  const isPayOrConsentWallDisabled =
    disablePayOrConsentWall || route.site === 'xing-login';
  const shouldHoldPayOrConsentUi =
    !isPayOrConsentWallDisabled && shouldHoldConsentUi;
  const shouldRenderPayOrConsentWall =
    !isPayOrConsentWallDisabled &&
    !shouldHoldConsentUi &&
    shouldRenderPayOrConsentPlaceholder;
  const cookieConsentUsercentrics =
    !isPayOrConsentWallDisabled && shouldLoadUsercentricsInBackground ? (
      <CookieConsentUsercentrics disableBanner loadInBackground />
    ) : (
      <CookieConsentUsercentrics disableBanner={disableCookieConsentBanner} />
    );

  // available for logged in users only, redirect to login page
  if (isLoggedOut && !visibleTo.loggedOut) {
    return host.redirectToLogin();
  }
  if (isSoftLoggedIn && !visibleTo.softLoggedIn) {
    return host.redirectToLogin();
  }

  // path does not go to any xinglet (logged in + logged out)
  if (!visibleTo.loggedIn && !visibleTo.loggedOut) {
    return <NoAccessPage />;
  }
  // available for logged out users only
  if (isLoggedIn && !visibleTo.loggedIn) {
    return <NoAccessPage />;
  }

  if (host.isServer && isLoggedOut && cacheMaxAge) {
    host.setHeader(
      'cache-control',
      `max-age=0, s-maxage=${cacheMaxAge}, must-revalidate`
    );
  }

  if (shouldHoldPayOrConsentUi) {
    return (
      <>
        {cookieConsentUsercentrics}
        <LoadingSkeleton />
      </>
    );
  }

  if (shouldRenderPayOrConsentWall) {
    return (
      <>
        <PayOrConsent />
        {cookieConsentUsercentrics}
      </>
    );
  }

  return (
    <>
      {cookieConsentUsercentrics}
      <ActiveItemContext.Provider value={activeItem}>
        <LayoutSwitcher
          layout={
            (isLoggedIn ? (layoutLoggedIn ?? layout) : layout) ?? DEFAULT_LAYOUT
          }
          loginState={loginState}
          loginDestination={loginDestination}
          loginTracking={loginTracking}
        >
          <XingletErrorBoundary fallback={<Generic />} xinglet={route.xinglet}>
            <Suspense fallback={<LoadingSkeleton />}>
              <RouteXinglet
                name={route.xinglet}
                nwtTrackingPage={nwtTrackingPage}
                nwtTrackingSentBy={nwtTrackingSentBy}
              >
                {children}
              </RouteXinglet>
            </Suspense>
          </XingletErrorBoundary>
        </LayoutSwitcher>
      </ActiveItemContext.Provider>
      <NoSSR>
        <XingletLoader
          name="@xing-com/crate-marketing-braze"
          ttt={ttt}
          section={brazeSection}
        />
        <ScrollManager />
        <OnboardingEnforcementExperiment />
      </NoSSR>
    </>
  );
};
